import Badge from "@/src/components/atom/Badge";
import Typo from "@/src/components/atom/Typo";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import { PaymentMethodDto } from "@/src/store/apis/subscription/interface";
import {
  formatCreditCardExpiryDate,
  formatCreditCardMasking,
} from "@/src/utils/format";
import { getCardTypeAsync } from "@/src/utils/cardUtils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import colorSet from "@/src/styles/color";

interface CardInfoDisplayProps {
  /** 결제 수단 정보 */
  payment: PaymentMethodDto;
  /** 법인/개인 구분 */
  isBusiness: boolean;
  /** 추가 스타일링 */
  className?: string;
}

/**
 * 카드 정보 표시 컴포넌트
 * 
 * PaymentManagement와 SubscriptionPlanModal에서 재사용
 */
const CardInfoDisplay: React.FC<CardInfoDisplayProps> = ({
  payment,
  isBusiness,
  className,
}) => {
  const { t } = useTranslation();
  const [cardTypeInfo, setCardTypeInfo] = useState<{
    type: string;
    icon: string | null;
    displayName: string;
  } | undefined>(undefined);

  const { cardNumber, expiryDate } = payment;

  useEffect(() => {
    getCardTypeAsync(cardNumber).then((type) => {
      setCardTypeInfo(type);
    });
  }, [cardNumber]);

  return (
    <Container className={className}>
      <CardContent>
        <CardHeader>
          {cardTypeInfo?.icon && (
            <CardIcon
              src={cardTypeInfo.icon}
              alt={`${cardTypeInfo.type} card`}
            />
          )}
          <CardTitle typoType="h4">{cardTypeInfo?.displayName}</CardTitle>
        </CardHeader>
      
      <CardInfoSection>
        <SectionCardRow
          label={
            <Typo color="gray6" typoType="b7m">
              {t("subscriptionManagement:subscription.cardInformation")}
            </Typo>
          }
          value={
            <CardInfoValue>
              <Typo typoType="b7r">
                {formatCreditCardMasking(cardNumber)}
              </Typo>
              <Badge
                badgeSize="S"
                badgeColor="gray10"
                color="gray4"
                text={
                  isBusiness
                    ? t("subscriptionManagement:subscription.business")
                    : t("subscriptionManagement:subscription.individual")
                }
              />
            </CardInfoValue>
          }
        />
        <SectionCardRow
          label={
            <Typo color="gray6" typoType="b7m">
              {t("subscriptionManagement:subscription.cardExpirationDate")}
            </Typo>
          }
          value={
            <Typo typoType="b7r">
              {formatCreditCardExpiryDate(expiryDate)}
            </Typo>
          }
        />
        </CardInfoSection>
      </CardContent>
    </Container>
  );
};

export default CardInfoDisplay;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CardContent = styled.div`
  padding: 20px;
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CardIcon = styled.img`
  width: 28px;
  height: 28px;
`;

const CardTitle = styled(Typo)`
  /* 카드 타입명 스타일링 */
`;


const CardInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardInfoValue = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;